import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProductTabs = ({ product = {} }) => {
  const [activeTab, setActiveTab] = useState('description');

  // No hardcoded fallback.
  const defaultProduct = {};

  const productData = { ...defaultProduct, ...product };

  const reviews = [
  {
    id: 1,
    author: 'Sarah M.',
    rating: 5,
    date: '2025-11-15',
    verified: true,
    title: 'Amazing results!',
    content: 'This serum has completely transformed my skin. I noticed a difference in hydration within just a few days, and after 2 weeks my skin looks so much brighter and smoother.',
    helpful: 24,
    images: [
    {
      url: "/assets/images/template/img-14cdf18c.webp",
      alt: 'Before and after skin comparison showing improved texture and glow'
    }]

  },
  {
    id: 2,
    author: 'Jessica L.',
    rating: 4,
    date: '2025-11-12',
    verified: true,
    title: 'Great for sensitive skin',
    content: 'I have very sensitive skin and this serum didn\'t cause any irritation. It\'s lightweight and absorbs quickly. The only reason I\'m not giving 5 stars is the price point.',
    helpful: 18
  },
  {
    id: 3,
    author: 'Maria R.',
    rating: 5,
    date: '2025-11-10',
    verified: false,
    title: 'Worth every penny',
    content: 'I\'ve tried many serums and this is by far the best. The texture is perfect - not sticky or heavy. My makeup applies so much better now.',
    helpful: 31
  }];


  const tabs = [
  { id: 'description', label: 'Description', icon: 'FileText' },
  { id: 'ingredients', label: 'Ingredients', icon: 'Leaf' },
  { id: 'usage', label: 'How to Use', icon: 'Info' },
  { id: 'reviews', label: 'Reviews', icon: 'Star', count: reviews?.length },
  { id: 'specifications', label: 'Specifications', icon: 'Settings' },
  { id: 'shipping', label: 'Shipping', icon: 'Truck' }];


  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars?.push(
        <Icon
          key={i}
          name="Star"
          size={14}
          className={i <= rating ? "text-warning fill-current" : "text-muted-foreground"} />

      );
    }
    return stars;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'description':
        return (
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-line text-foreground leading-relaxed">
              {productData?.description}
            </div>
          </div>);


      case 'ingredients':
        return (
          <div>
            <h4 className="font-medium text-foreground mb-3">Full Ingredient List</h4>
            <p className="text-sm text-foreground leading-relaxed">
              {productData?.ingredients}
            </p>
            <div className="mt-4 p-4 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} className="text-accent flex-shrink-0 mt-0.5" />
                <p className="text-sm text-accent-foreground">
                  Always patch test new products. Discontinue use if irritation occurs.
                </p>
              </div>
            </div>
          </div>);


      case 'usage':
        return (
          <div>
            <h4 className="font-medium text-foreground mb-3">Application Instructions</h4>
            <div className="whitespace-pre-line text-sm text-foreground leading-relaxed">
              {productData?.usage}
            </div>
          </div>);


      case 'reviews':
        return (
          <div className="space-y-6">
            {/* Review Summary */}
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <div className="flex items-center space-x-4">
                <div className="text-3xl font-bold text-foreground">4.8</div>
                <div>
                  <div className="flex items-center space-x-1 mb-1">
                    {renderStars(5)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Based on {reviews?.length} reviews
                  </p>
                </div>
              </div>
              <button className="text-sm text-primary hover:underline">
                Write a review
              </button>
            </div>
            {/* Reviews List */}
            <div className="space-y-6">
              {reviews?.map((review) =>
              <div key={review?.id} className="border-b border-border pb-6 last:border-b-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-foreground">{review?.author}</span>
                        {review?.verified &&
                      <span className="bg-success/10 text-success text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                            <Icon name="CheckCircle" size={12} />
                            <span>Verified</span>
                          </span>
                      }
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          {renderStars(review?.rating)}
                        </div>
                        <span className="text-sm text-muted-foreground">{review?.date}</span>
                      </div>
                    </div>
                  </div>
                  
                  <h5 className="font-medium text-foreground mb-2">{review?.title}</h5>
                  <p className="text-sm text-foreground mb-3 leading-relaxed">
                    {review?.content}
                  </p>
                  
                  {review?.images && review?.images?.length > 0 &&
                <div className="flex space-x-2 mb-3">
                      {review?.images?.map((image, index) =>
                  <div key={index} className="w-16 h-16 rounded-lg overflow-hidden">
                          <Image
                      src={image?.url}
                      alt={image?.alt}
                      className="w-full h-full object-cover" />

                        </div>
                  )}
                    </div>
                }
                  
                  <div className="flex items-center space-x-4 text-sm">
                    <button className="flex items-center space-x-1 text-muted-foreground hover:text-foreground">
                      <Icon name="ThumbsUp" size={14} />
                      <span>Helpful ({review?.helpful})</span>
                    </button>
                    <button className="text-muted-foreground hover:text-foreground">
                      Reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>);


      case 'specifications':
        return (
          <div className="space-y-4">
            {Object.entries(productData?.specifications || {})?.map(([key, value]) =>
            <div key={key} className="flex justify-between py-2 border-b border-border last:border-b-0">
                <span className="font-medium text-foreground">{key}</span>
                <span className="text-muted-foreground text-right">{value}</span>
              </div>
            )}
          </div>);


      case 'shipping':
        return (
          <div className="space-y-4">
            {Object.entries(productData?.shipping || {})?.map(([key, value]) =>
            <div key={key} className="flex justify-between py-2 border-b border-border last:border-b-0">
                <span className="font-medium text-foreground">{key}</span>
                <span className="text-muted-foreground text-right">{value}</span>
              </div>
            )}
          </div>);


      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-elevation-1">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) =>
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
            activeTab === tab?.id ?
            'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
            }>

              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
              {tab?.count &&
            <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full">
                  {tab?.count}
                </span>
            }
            </button>
          )}
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>);

};

export default ProductTabs;